import React from "react";
import { Link, useLocation } from "react-router-dom";
import { 
  BookOpen, 
  CreditCard,
  UserRound,
  Home,
  GamepadIcon,
  Users,
  HelpCircle,
  MessageSquare,
  UsersRound,
  FileText,
  Gamepad2,
  Crosshair,
  Group
} from "lucide-react";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger 
} from "@/components/ui/tooltip";

// Link definition types
export interface SidebarLink {
  to: string;
  label: string;
  icon: React.ReactNode;
}

// Helper function to determine active links
export const useActiveLinkChecker = () => {
  const location = useLocation();
  
  return (path: string) => {
    if (path === '/painel-jogador' && location.pathname !== '/painel-jogador') {
      return false;
    }
    return location.pathname.startsWith(path);
  };
};

// Player panel links
export const usePlayerLinks = (): SidebarLink[] => {
  return [
    {
      to: '/painel-jogador/mentorias-compradas',
      label: 'Mentorias Compradas',
      icon: <BookOpen className="w-5 h-5" />
    },
    {
      to: '/painel-jogador/meus-conteudos',
      label: 'Meus Conteúdos',
      icon: <FileText className="w-5 h-5" />
    },
    {
      to: '/painel-jogador/minhas-comunidades',
      label: 'Minhas Comunidades',
      icon: <UsersRound className="w-5 h-5" />
    },
    {
      to: '/painel-jogador/chat',
      label: 'Chat',
      icon: <MessageSquare className="w-5 h-5" />
    },
    {
      to: '/painel-jogador/pagamentos',
      label: 'Histórico de Pagamentos',
      icon: <CreditCard className="w-5 h-5" />
    },
    {
      to: '/painel-jogador/perfil',
      label: 'Perfil',
      icon: <UserRound className="w-5 h-5" />
    }
  ];
};

// Main navigation links
export const useMainLinks = (): SidebarLink[] => {
  return [
    {
      to: '/',
      label: 'Início',
      icon: <Home className="w-5 h-5" />
    },
    {
      to: '/mentores',
      label: 'Mentores',
      icon: <Users className="w-5 h-5" />
    },
    {
      to: '/jogos',
      label: 'Jogos',
      icon: <Gamepad2 className="w-5 h-5" />
    },
    {
      to: '/mentorias',
      label: 'Mentorias',
      icon: <Crosshair className="w-5 h-5" />
    },
    {
      to: '/comunidades',
      label: 'Comunidades',
      icon: <Group className="w-5 h-5" />
    },
    {
      to: '/como-funciona',
      label: 'Como Funciona',
      icon: <HelpCircle className="w-5 h-5" />
    }
  ];
};

interface LinksSectionProps {
  links: SidebarLink[];
  isCollapsed: boolean;
}

// Component for rendering links with tooltip support
export const LinksSection: React.FC<LinksSectionProps> = ({ links, isCollapsed }) => {
  const isLinkActive = useActiveLinkChecker();

  return (
    <TooltipProvider>
      {links.map((link) => (
        <Tooltip key={link.to}>
          <TooltipTrigger asChild>
            <Link
              to={link.to}
              className={`flex items-center gap-3 px-3 py-2.5 mb-1 rounded-lg transition-all ${
                isLinkActive(link.to) 
                  ? "bg-gradient-to-r from-[#182437] to-[#1E293B] text-[#00B6F0] border-l-4 border-[#00B6F0]" 
                  : "text-gray-400 hover:text-white hover:bg-[#1A2233]"
              }`}
            >
              <span className={`${isLinkActive(link.to) ? "text-[#00B6F0]" : "text-gray-400"}`}>
                {link.icon}
              </span>
              {!isCollapsed && (
                <span className="font-medium truncate">{link.label}</span>
              )}
              {isLinkActive(link.to) && !isCollapsed && (
                <span className="ml-auto h-2 w-2 rounded-full bg-[#00B6F0]"></span>
              )}
            </Link>
          </TooltipTrigger>
          {isCollapsed && (
            <TooltipContent side="right">
              {link.label}
            </TooltipContent>
          )}
        </Tooltip>
      ))}
    </TooltipProvider>
  );
};
